import {
  PAGE_SLUG,
  REGISTRY_BLOCK,
  isPdlMissingValue,
  resolveFooterLegalLinks,
  resolveNavLogoSurface,
  retargetPlatformNavLogoPathForSurface,
  type LayoutBlock,
  type PageMeta,
} from '@web-studio/shared-schemas';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet, canonicalizeMediaUrl } from '../lib/imageProxy';
import { pickBlockField } from '../lib/localized';
import { SITE_IMAGE_SIZES } from '../lib/responsiveImageSizes';
import { pickChrome } from '../lib/siteChromeCopy';
import { parseHoursSchedule } from '../lib/splitHoursLines';
import { escapeHtml } from './escape';
import type { BlockHtmlRenderContext } from './renderSiteHtml';
import { renderSiteSocialLinksHtml } from './siteSocialHtml';

type FooterHtmlRenderer = (block: LayoutBlock, ctx: BlockHtmlRenderContext) => string;

function pickFooterContactField(
  content: LayoutBlock['content'],
  key: string,
  locale: string,
): string {
  const value = pickBlockField(content, key, locale);
  return isPdlMissingValue(value) ? '' : value;
}

function renderHoursScheduleHtml(hours: string, className = 'ws-hours-schedule'): string {
  const rows = parseHoursSchedule(hours);
  if (rows.length === 0) {
    return '';
  }
  return `<div class="${className}">${rows
    .map(
      (row) =>
        `<div class="ws-hours-schedule__row"><span class="ws-hours-schedule__day">${escapeHtml(row.day)}</span><span class="ws-hours-schedule__time">${escapeHtml(row.time)}</span></div>`,
    )
    .join('')}</div>`;
}

function renderFooterSocialHtml(
  content: LayoutBlock['content'],
  locale: string,
  options?: { showHeading?: boolean; wrapClass?: string },
): string {
  return renderSiteSocialLinksHtml(content, locale, {
    surface: 'footer',
    showHeading: options?.showHeading,
    wrapClass: options?.wrapClass,
  });
}

function renderFooterLegalClusterHtml(
  block: LayoutBlock,
  locale: string,
  pages: PageMeta[] | undefined,
  defaultLocale: string,
  publicPathPrefix?: string | null,
): { legalNavHtml: string; legalLineHtml: string } {
  const legalLine = pickBlockField(block.content, 'legalLine', locale);
  const legalLinks = resolveFooterLegalLinks(pages, locale, defaultLocale, publicPathPrefix);
  const imprintOverride = pickBlockField(block.content, 'imprintLabel', locale).trim();
  const privacyOverride = pickBlockField(block.content, 'privacyLabel', locale).trim();
  const cookieSettingsLabel = escapeHtml(
    pickBlockField(block.content, 'cookieSettingsLabel', locale).trim() ||
      pickChrome('cookieSettingsLabel', locale),
  );
  const legalLinksHtml = [
    ...legalLinks.map((link) => {
      const label =
        link.slug === PAGE_SLUG.impressum
          ? imprintOverride || link.label
          : link.slug === PAGE_SLUG.datenschutz
            ? privacyOverride || link.label
            : link.label;
      return `<li><a class="ws-footer__legal-link" href="${escapeHtml(link.href)}">${escapeHtml(label)}</a></li>`;
    }),
    `<li><button type="button" class="ws-footer__legal-link ws-footer__cookie-settings" data-ws-cookie-settings-open>${cookieSettingsLabel}</button></li>`,
  ].join('');
  return {
    legalNavHtml: `<nav class="ws-footer__legal-nav" aria-label="${escapeHtml(pickChrome('legalNav', locale))}"><ul class="ws-footer__legal-links">${legalLinksHtml}</ul></nav>`,
    legalLineHtml: legalLine
      ? `<span class="ws-footer__legal-line">${escapeHtml(legalLine)}</span>`
      : '',
  };
}

export function renderFooterStandardHtml(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, pages, defaultLocale, publicPathPrefix } = ctx;
  const siteTitle = pickBlockField(block.content, 'siteTitle', locale);
  const address = pickFooterContactField(block.content, 'address', locale);
  const phone = pickFooterContactField(block.content, 'phone', locale);
  const email = pickFooterContactField(block.content, 'email', locale);
  const hours = pickFooterContactField(block.content, 'hours', locale);
  const hoursHtml = renderHoursScheduleHtml(hours);
  const socialHtml = renderFooterSocialHtml(block.content, locale);
  const { legalNavHtml, legalLineHtml } = renderFooterLegalClusterHtml(
    block,
    locale,
    pages,
    defaultLocale,
    publicPathPrefix,
  );

  const phoneHtml = phone
    ? `<p class="ws-footer__text"><a href="tel:${escapeHtml(phone.replace(/\s/g, ''))}">${escapeHtml(phone)}</a></p>`
    : '';
  const emailHtml = email
    ? `<p class="ws-footer__text"><a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a></p>`
    : '';

  return `<footer class="ws-footer" data-block-id="${escapeHtml(block.blockId)}" data-component="Footer_Standard" data-footer-arrangement="standard" id="contact">
  <div class="ws-footer__inner">
    <div class="ws-footer__group ws-footer__group--brand">${siteTitle ? `<p class="ws-footer__brand">${escapeHtml(siteTitle)}</p>` : ''}${address ? `<p class="ws-footer__text">${escapeHtml(address)}</p>` : ''}${socialHtml}</div>
    <div class="ws-footer__group ws-footer__group--contact"><p class="ws-footer__heading">${escapeHtml(pickChrome('footerContact', locale))}</p>${phoneHtml}${emailHtml}</div>
    <div class="ws-footer__group ws-footer__group--hours"><p class="ws-footer__heading">${escapeHtml(pickChrome('footerHours', locale))}</p>${hoursHtml}</div>
    <div class="ws-footer__legal">${legalNavHtml}${legalLineHtml}</div>
  </div>
</footer>`;
}

function renderFooterColophonHtml(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, pages, defaultLocale, publicPathPrefix, themeId, colorsetId } = ctx;
  const siteTitle = pickBlockField(block.content, 'siteTitle', locale);
  const address = pickFooterContactField(block.content, 'address', locale);
  const phone = pickFooterContactField(block.content, 'phone', locale);
  const email = pickFooterContactField(block.content, 'email', locale);
  const hours = pickFooterContactField(block.content, 'hours', locale);
  const hoursHtml = renderHoursScheduleHtml(hours, 'ws-footer-colophon__hours ws-hours-schedule');
  const socialHtml = renderFooterSocialHtml(block.content, locale, {
    wrapClass: 'ws-footer-colophon__social',
  });
  const { legalNavHtml, legalLineHtml } = renderFooterLegalClusterHtml(
    block,
    locale,
    pages,
    defaultLocale,
    publicPathPrefix,
  );
  const phoneHtml = phone
    ? `<p class="ws-footer-colophon__meta-item"><a href="tel:${escapeHtml(phone.replace(/\s/g, ''))}">${escapeHtml(phone)}</a></p>`
    : '';
  const emailHtml = email
    ? `<p class="ws-footer-colophon__meta-item"><a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a></p>`
    : '';

  const logoImageRaw = block.media?.logoImage?.trim() ?? '';
  const logoCanonical = logoImageRaw ? canonicalizeMediaUrl(logoImageRaw) : '';
  const logoSurface = resolveNavLogoSurface(themeId, null, colorsetId);
  const logoImage = logoCanonical
    ? retargetPlatformNavLogoPathForSurface(logoCanonical, logoSurface)
    : '';
  const logoSrc = logoImage ? buildSiteImageProxyUrl(logoImage, 240, 78) : '';
  const logoSrcSet = logoImage ? buildSiteImageSrcSet(logoImage, [120, 180, 240], 78) : '';
  const markHtml = logoSrc
    ? `<div class="ws-footer-colophon__mark"><img class="ws-footer-colophon__mark-img" src="${escapeHtml(logoSrc)}" srcset="${escapeHtml(logoSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.heroMonolithSeal)}" alt="${escapeHtml(siteTitle || 'Mark')}" width="120" height="120" loading="lazy" decoding="async" /></div>`
    : '';
  const brandHtml = siteTitle
    ? `<p class="ws-footer-colophon__brand">${escapeHtml(siteTitle)}</p>`
    : '';
  const identityHtml =
    markHtml || brandHtml
      ? `<div class="ws-footer-colophon__identity">${markHtml}${brandHtml}</div>`
      : '';

  return `<footer class="ws-footer-colophon" data-block-id="${escapeHtml(block.blockId)}" data-component="Footer_Standard" data-footer-arrangement="colophon" id="contact">
  <div class="ws-footer-colophon__inner">
    ${identityHtml}
    ${address ? `<p class="ws-footer-colophon__address">${escapeHtml(address)}</p>` : ''}
    <div class="ws-footer-colophon__meta">${phoneHtml}${emailHtml}</div>
    ${hoursHtml}
    ${socialHtml}
    <div class="ws-footer-colophon__legal">${legalNavHtml}${legalLineHtml}</div>
  </div>
</footer>`;
}

function renderFooterDirectoryHtml(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, pages, defaultLocale, publicPathPrefix } = ctx;
  const siteTitle = pickBlockField(block.content, 'siteTitle', locale);
  const address = pickFooterContactField(block.content, 'address', locale);
  const phone = pickFooterContactField(block.content, 'phone', locale);
  const email = pickFooterContactField(block.content, 'email', locale);
  const hours = pickFooterContactField(block.content, 'hours', locale);
  const hoursHtml = renderHoursScheduleHtml(hours);
  const socialHtml = renderFooterSocialHtml(block.content, locale, {
    wrapClass: 'ws-footer-directory__social',
  });
  const { legalNavHtml, legalLineHtml } = renderFooterLegalClusterHtml(
    block,
    locale,
    pages,
    defaultLocale,
    publicPathPrefix,
  );
  const phoneHtml = phone
    ? `<p class="ws-footer__text"><a href="tel:${escapeHtml(phone.replace(/\s/g, ''))}">${escapeHtml(phone)}</a></p>`
    : '';
  const emailHtml = email
    ? `<p class="ws-footer__text"><a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a></p>`
    : '';

  return `<footer class="ws-footer-directory" data-block-id="${escapeHtml(block.blockId)}" data-component="Footer_Standard" data-footer-arrangement="directory" id="contact">
  <div class="ws-footer-directory__inner">
    <div class="ws-footer-directory__grid">
      <div class="ws-footer-directory__cell ws-footer-directory__cell--brand">
        ${siteTitle ? `<p class="ws-footer-directory__brand">${escapeHtml(siteTitle)}</p>` : ''}
        ${address ? `<p class="ws-footer-directory__address">${escapeHtml(address)}</p>` : ''}
        ${socialHtml}
      </div>
      <div class="ws-footer-directory__cell"><p class="ws-footer__heading">${escapeHtml(pickChrome('footerContact', locale))}</p>${phoneHtml}${emailHtml}</div>
      <div class="ws-footer-directory__cell"><p class="ws-footer__heading">${escapeHtml(pickChrome('footerHours', locale))}</p>${hoursHtml}</div>
    </div>
    <div class="ws-footer-directory__legal">${legalNavHtml}${legalLineHtml}</div>
  </div>
</footer>`;
}

function renderFooterUtilityHtml(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale, pages, defaultLocale, publicPathPrefix } = ctx;
  const siteTitle = pickBlockField(block.content, 'siteTitle', locale);
  const address = pickFooterContactField(block.content, 'address', locale);
  const phone = pickFooterContactField(block.content, 'phone', locale);
  const email = pickFooterContactField(block.content, 'email', locale);
  const hours = pickFooterContactField(block.content, 'hours', locale);
  const hoursHtml = renderHoursScheduleHtml(hours, 'ws-footer-utility__hours ws-hours-schedule');
  const socialHtml = renderFooterSocialHtml(block.content, locale, {
    wrapClass: 'ws-footer-utility__social',
  });
  const { legalNavHtml, legalLineHtml } = renderFooterLegalClusterHtml(
    block,
    locale,
    pages,
    defaultLocale,
    publicPathPrefix,
  );
  const phoneHtml = phone
    ? `<p class="ws-footer-utility__action"><a href="tel:${escapeHtml(phone.replace(/\s/g, ''))}">${escapeHtml(phone)}</a></p>`
    : '';
  const emailHtml = email
    ? `<p class="ws-footer-utility__action"><a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a></p>`
    : '';

  return `<footer class="ws-footer-utility" data-block-id="${escapeHtml(block.blockId)}" data-component="Footer_Standard" data-footer-arrangement="utility" id="contact">
  <div class="ws-footer-utility__inner">
    <div class="ws-footer-utility__dock">
      <div class="ws-footer-utility__identity">
        ${siteTitle ? `<p class="ws-footer-utility__brand">${escapeHtml(siteTitle)}</p>` : ''}
        ${socialHtml}
      </div>
      <div class="ws-footer-utility__actions">${phoneHtml}${emailHtml}</div>
    </div>
    ${address ? `<p class="ws-footer-utility__address">${escapeHtml(address)}</p>` : ''}
    ${hoursHtml}
    <div class="ws-footer-utility__legal">${legalNavHtml}${legalLineHtml}</div>
  </div>
</footer>`;
}

const P = REGISTRY_BLOCK.presentation;

export const FOOTER_SSR_REGISTRY_OVERRIDES: Partial<Record<string, FooterHtmlRenderer>> = {
  [P.footerStandard]: renderFooterStandardHtml,
  [P.footerColophon]: renderFooterColophonHtml,
  [P.footerDirectory]: renderFooterDirectoryHtml,
  [P.footerUtility]: renderFooterUtilityHtml,
};
